import type { AxiosRequestConfig } from 'axios'
import type { SensitiveApprovalDetailRow, SensitiveApprovalResponse } from './types'

export const APPROVAL_SUBMIT_HEADER = 'X-Approval-Submit'
export const APPROVAL_REASON_HEADER = 'X-Approval-Reason'
export const APPROVAL_REQUEST_ID_HEADER = 'X-Approval-Request-Id'
export const APPROVAL_BUSINESS_NAME_HEADER = 'X-Approval-Business-Name'
export const APPROVAL_DETAIL_ROWS_HEADER = 'X-Approval-Detail-Rows'
export const SENSITIVE_APPROVAL_REPLAY_FLAG = '__sensitiveApprovalReplay'

const MAX_APPROVAL_DETAIL_ROWS = 30
const MAX_APPROVAL_DETAIL_ROW_LABEL_LENGTH = 32
const MAX_APPROVAL_DETAIL_ROW_VALUE_LENGTH = 200
const MAX_APPROVAL_DETAIL_ROWS_HEADER_LENGTH = 6000

interface ApprovalReplayContextConfig extends AxiosRequestConfig {
  sensitiveApprovalBusinessName?: string
  sensitiveApprovalDetailRows?: SensitiveApprovalDetailRow[]
}

export function buildApprovalReplayConfig(
  config: AxiosRequestConfig,
  approval: Pick<SensitiveApprovalResponse, 'requestId' | 'businessName' | 'businessId' | 'approvalDetailRows'>,
  reason: string,
): AxiosRequestConfig {
  const businessName = getApprovalBusinessName(config, approval)
  const detailRows = normalizeApprovalDetailRows(
    approval.approvalDetailRows || (config as ApprovalReplayContextConfig).sensitiveApprovalDetailRows || [],
  )
  const detailRowsHeader = detailRows.length ? encodeDetailRowsHeader(detailRows) : ''
  const replayConfig: AxiosRequestConfig = {
    ...config,
    headers: {
      ...(config.headers ?? {}),
      [APPROVAL_SUBMIT_HEADER]: 'true',
      [APPROVAL_REASON_HEADER]: encodeURIComponent(reason.trim()),
      [APPROVAL_REQUEST_ID_HEADER]: approval.requestId ?? '',
      ...(businessName ? { [APPROVAL_BUSINESS_NAME_HEADER]: encodeURIComponent(businessName) } : {}),
      ...(detailRowsHeader ? { [APPROVAL_DETAIL_ROWS_HEADER]: detailRowsHeader } : {}),
    },
  }
  ;(replayConfig as Record<string, unknown>)[SENSITIVE_APPROVAL_REPLAY_FLAG] = true
  return replayConfig
}

function getApprovalBusinessName(
  config: AxiosRequestConfig,
  approval: Pick<SensitiveApprovalResponse, 'businessName' | 'businessId'>,
) {
  const explicitName = (config as ApprovalReplayContextConfig).sensitiveApprovalBusinessName?.trim()
  if (explicitName) return explicitName

  const businessName = approval.businessName?.trim()
  if (businessName && businessName !== String(approval.businessId ?? '')) {
    return businessName
  }
  return ''
}

function normalizeApprovalText(value: unknown, maxLength: number) {
  if (value === undefined || value === null || value === '') return ''
  return String(value).trim().slice(0, maxLength)
}

function normalizeApprovalDetailRows(rows: SensitiveApprovalDetailRow[]) {
  const seen = new Set<string>()
  return rows
    .slice(0, MAX_APPROVAL_DETAIL_ROWS)
    .map(row => ({
      label: normalizeApprovalText(row?.label, MAX_APPROVAL_DETAIL_ROW_LABEL_LENGTH),
      value: normalizeApprovalText(row?.value, MAX_APPROVAL_DETAIL_ROW_VALUE_LENGTH),
    }))
    .filter(row => row.label && row.value && row.value !== '--')
    .filter(row => {
      const key = `${row.label}:${row.value}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

function encodeDetailRowsHeader(rows: SensitiveApprovalDetailRow[]) {
  const encoded = encodeURIComponent(JSON.stringify(rows))
  return encoded.length > MAX_APPROVAL_DETAIL_ROWS_HEADER_LENGTH ? '' : encoded
}
